import React, { useEffect, useState } from "react";
import { Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Typography, Button, Space, Tooltip as AntTooltip, Spin, Empty } from "antd";
import { useSelector, useDispatch } from 'react-redux';
import { getInstituteChartData } from '../../../actions';
import arrowIcon from 'assets/arrow.png';
import jsPDF from "jspdf"
import { generateTopInstitutesPDF } from "../utils/generateTopInstitutesPDF";

const { Text } = Typography;

ChartJS.register(ArcElement, Tooltip, Legend);

const InstituteGender = ({ selectedInstitute, selectedFilter, topInstitutesRef, setIndividualExportLoading, setExportMessage, isApplied, selectedBarType }) => {
  const dispatch = useDispatch();
  const instituteChartData = useSelector(state => state?.corporateDashboardPage2?.instituteChartData);
  const corporateData = useSelector((state) => state?.auth?.corporateData)
  
  // Check if no institute is selected
  const isNoSelection = !selectedInstitute;

  // Check if there's any data for institutes (overall data, not just selected institute)
  const hasData = instituteChartData?.instituteBarChart?.some(item => 
        (item.hired || 0) > 0 || 
        (item.applicants || 0) > 0
    );
  
  // Fetch institute chart data when institute changes
  useEffect(() => {
    if (selectedInstitute && selectedFilter) {
      dispatch(getInstituteChartData(selectedFilter));
    }
  }, [dispatch, selectedInstitute, selectedFilter]);

  // Use API data instead of hardcoded data
  const apiData = instituteChartData?.instituteBarChart || [];
  
  // Sort API data the same way as InstitueBar to ensure index matching
  const sortedApiData = [...apiData].sort((a, b) => {
    if (isApplied) {
      return b.applicants - a.applicants; // descending by applicants
    } else {
      return b.hired - a.hired; // descending by hired
    }
  }).slice(0, 10); // Top 10
  
  // Find the gender data for the selected institute from the new API structure
  // Extract index from selectedInstitute if it's in format "institute_0", "institute_1", etc.
  let selectedIndex = null;
  if (selectedInstitute && selectedInstitute.startsWith('institute_')) {
    selectedIndex = parseInt(selectedInstitute.split('_')[1], 10);
  }
  
  const selectedInstituteData = selectedIndex !== null && selectedIndex < sortedApiData.length
    ? sortedApiData[selectedIndex]
    : null;
  
  // Extract gender data based on CLICKED BAR TYPE, not toggle state
  const genderData = selectedInstituteData ? (selectedBarType === 'applicants'
    ? selectedInstituteData?.applicantGenderAnalysis
    : selectedInstituteData?.hiredGenderAnalysis) || {
    men: 0,
    women: 0,
    others: 0,
    totalCandidate: 0,
    malePercent: 0,
    femalePercent: 0,
    othersPercent: 0
  } : {
    men: 0,
    women: 0,
    others: 0,
    totalCandidate: 0,
    malePercent: 0,
    femalePercent: 0,
    othersPercent: 0
  };
  
  const { men, women, others, totalCandidate, malePercent, femalePercent, othersPercent } = genderData;
  const total = men + women + others;
  
  // Use API percentages if available, otherwise calculate
  const menPercentage = total > 0 ? (malePercent || Math.round((men / total) * 100)) : 0;
  const womenPercentage = total > 0 ? (femalePercent || Math.round((women / total) * 100)) : 0;
  const othersPercentage = total > 0 ? (othersPercent || Math.round((others / total) * 100)) : 0;

  // Get the institute name for display
  const selectedInstituteName = selectedIndex !== null && selectedIndex < sortedApiData.length
    ? sortedApiData[selectedIndex]?.institute || 'Unknown'
    : null;

  // === Data from API ===
  const chartData = {
    labels: (isNoSelection || total === 0) ? [''] : (others > 0 ? ["Men", "Women", "Others"] : ["Men", "Women"]),
    datasets: [
      {
        data: isNoSelection ? [100] : (total === 0 ? [100] : (others > 0 ? [men, women, others] : [men, women])),
        backgroundColor: isNoSelection ? ['#d9d9d9'] : (total === 0 ? ['#d9d9d9'] : (others > 0 ? ["rgba(13, 58, 133, 1)", "rgba(1, 138, 190, 1)", "rgba(145, 225, 255, 1)"] : ["rgba(13, 58, 133, 1)", "rgba(1, 138, 190, 1)", "rgba(145, 225, 255, 1)"])),
        borderWidth: 0,
        cutout: "65%", // gives thinner donut ring like your image
      },
    ],
  };

  // === Chart Options ===
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      datalabels: { display: false },
      tooltip: {
        callbacks: {
          // Show number with commas in tooltip, but show 0 when no selection or no data
          label: (context) => {
            if (isNoSelection || total === 0) {
              return `: 0`;
            }
            return `${context.label}: ${context.parsed.toLocaleString()}`;
          }, 
        },
      },
    },
  };

  const handleIndividualExport = async () => {
    try {
      setIndividualExportLoading(true);
      setExportMessage('Exporting Institute Data...');
      const pdf = new jsPDF("p", "mm", "a4")

      await generateTopInstitutesPDF({
        pdf,
        corporateData,
        instituteChartData,
        topInstitutesRef: topInstitutesRef,
      })

      pdf.save("Institute_Data.pdf")
    } catch (error) {
      console.error("Export failed:", error)
      // You could add user notification here
    } finally {
      setIndividualExportLoading(false);
    }
  }

  return (
    <div style={{
      width: "100%",
      height: 500,
      display: "flex",
      flexDirection: "column",
      position: "relative",
      justifyContent: "flex-start",
    }}>
      {/* === Header === */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: 8,
          gap: 8,
        }}
      >
        <Text style={{ fontSize: 20, fontWeight: 600, margin: 0, color: '#000' }}>
          Gender Analysis{selectedInstituteName && " – "}
          {selectedInstituteName && (
            <AntTooltip 
              title={selectedInstituteName}
              getPopupContainer={(triggerNode) => triggerNode.parentElement}
            >
              <span style={{ color: "rgba(52, 62, 89, 1)", fontFamily: 'Plus Jakarta Sans', weight: '400' }}>
                {selectedInstituteName.length > 20
                  ? `${selectedInstituteName.substring(0, 20)}...`
                  : selectedInstituteName}
              </span>
            </AntTooltip>
          )}
        </Text>
          <Button
            type="default"
            onClick={handleIndividualExport}
            disabled={!hasData}
            style={{
              borderColor: "#00a878",
              color: "#00a878",
              fontWeight: 500,
              borderRadius: 6,
              height: 30,
              marginLeft: "auto",
            }}
          >
            Export
          </Button>
      </div>

      {hasData ? (
        <>
      {/* === Donut + Info === */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          gap: 40,
          marginTop: 40,
          flexWrap: "wrap",
        }}
      >
        {/* Donut Chart */}
        <div style={{ 
          position: "relative", 
          width: 280, 
          height: 280, 
          marginLeft: 80,
          marginTop: 20,    
          transition: "all 0.3s ease",  }}>

          <Doughnut data={chartData} options={options} />
        </div>

        {/* Info Labels */}
        {selectedInstitute && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 24,
              justifyContent: "center",
            }}
          >
            {/* Men */}
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: "rgba(13, 58, 133, 1)",
                }}
              ></div>
              <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
                <Text strong style={{fontSize: 20,fontWeight: '500', fontFamily: 'Plus Jakarta Sans', color:'rgba(0, 0, 0, 1)'}}>Men</Text>
                <Text strong style={{ fontSize: 18 }}>{menPercentage}%</Text>
              </div>
            </div>

            {/* Women */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <div
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: "rgba(1, 138, 190, 1)",
                }}
              ></div>
              <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
                <Text strong style={{fontSize: 20,fontWeight: '500', fontFamily: 'Plus Jakarta Sans', color:'rgba(0, 0, 0, 1)'}}>Women</Text>
                <Text strong style={{ fontSize: 18 }}>{womenPercentage}%</Text>
              </div>
            </div>

            {/* Others */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <div
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: "rgba(145, 225, 255, 1)",
                }}
              ></div>
              <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
                <Text strong style={{fontSize: 20,fontWeight: '500', fontFamily: 'Plus Jakarta Sans', color:'rgba(0, 0, 0, 1)'}}>Others</Text>
                <Text strong style={{ fontSize: 18 }}>{othersPercentage}%</Text>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* === Bottom Info Box === */}
      <div
        style={{
          marginTop: selectedInstitute ? 57 : 52,
          backgroundColor: "#fff",
          border: selectedInstitute ? "1px solid #435fe8" : "1px solid rgba(194, 194, 194, 1)",
          borderRadius: '8px',
          padding: '12px 16px',
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {selectedInstitute ? (
          <>
            <Text strong type="secondary" style={{ fontSize: 14, display: 'flex-start' }}>
              Showing
            </Text>

            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div
                style={{
                  width: 10,
                  height: 10,
                  backgroundColor: "rgba(0, 27, 72, 1)",
                  borderRadius: "50%",
                }}
              ></div>
              <Text style={{ fontSize: 13 }}>
                <strong>Men:</strong> {men.toLocaleString()}
              </Text>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div
                style={{
                  width: 10,
                  height: 10,
                  backgroundColor: "rgba(1, 138, 190, 1)",
                  borderRadius: "50%",
                }}
              ></div>
              <Text style={{ fontSize: 13 }}>
                <strong>Women:</strong> {women.toLocaleString()}
              </Text>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div
                  style={{
                    width: 10,
                    height: 10,
                    backgroundColor: "#rgba(145, 225, 255, 1)",
                    borderRadius: "50%",
                  }}
                ></div>
                <Text style={{ fontSize: 13 }}>
                  <strong>Others:</strong> {others.toLocaleString()}
                </Text>
              </div>
          </>
        ) : (
          <Space size={8}>
            <svg width="18" height="21" viewBox="0 0 18 21" style={{ marginLeft: '2px' }}>
              <rect 
                x="10.05" y="3" width="4.022172927856445" height="12" fill="rgba(50, 73, 215, 1)"opacity="1"
              />
              <rect 
                x="10.05" y="17" width="4.022172927856445" height="4" fill="rgba(50, 73, 215, 1)"opacity="1"
              />
            </svg>
            <Text style={{ 
              color: '#262626',
              fontFamily: 'Plus Jakarta Sans',fontWeight: 400,fontSize: 14,lineHeight: '18px',letterSpacing: '0%'
            }}>
              Note: Click the graph for more details.
            </Text>
          </Space>
        )}
      </div>
        </>
      ) : (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px', flexGrow: 1 }}>
          <Empty />
        </div>
      )}
    </div>
  );
};

export default InstituteGender;
